<template>
  <el-container class="layout-container">
    <!-- 左侧菜单 -->
    <!-- <el-aside width="asideWidth + 'px'" class="resizable-aside"> -->
    <el-aside
      :width="`${asideWidth}px`"
      class="resizable-aside"
      :class="{ 'is-dragging': isDragging }"
    >
      <Sidebar
        v-model:menu-temp="menuTemp"
        :menu-list="menuList"
        @menu-action="handleMenuAction"
      />
      <!-- 优化拖拽条 -->
      <div
        class="resize-handle"
        @mousedown.prevent="startDrag"
        :class="{ 'is-active': isDragging }"
      ></div>
    </el-aside>

    <!-- 右侧内容区 -->
    <el-main>
      <!-- 根据选中项类型显示不同的页面组件 -->
      <template v-if="menuTemp.type === 'terms'">
        <term-page
          :terms-data="menuTemp.terms || []"
          :standard-id="standardData.id"
          :standard-name="menuTemp.standardName"
          @update="handleUpdate"
        />
      </template>
      <template v-else-if="menuTemp.type === 'standard'">
        <standard-page
          :standard-data="{
            id: menuTemp.id || '',
            standardId: menuTemp.standardId || '',
            standardName: menuTemp.standardName || '',
            responsibleUnit: formData.responsibleUnit || '',
            standardNumber: formData.standardNumber || '',
            departmentName: formData.departmentName || '',
            draftAuthor: formData.draftAuthor || '',
            draftUnit: formData.draftUnit || '',
            scope: formData.scope || '',
            level: formData.level || '',
            normativeDocuments: formData.normativeDocuments || '',
            image: menuTemp.image || undefined,
            publicationDate: formData.publicationDate || '',
            implementationDate: formData.implementationDate || '',
            file: formData.file || '',
            fileName: formData.fileName || '',
          }"
          :terms-data="menuTemp.terms || []"
          @update="handleUpdate"
        />
      </template>
      <template v-else-if="menuTemp.type === 'level'">
        <level-page
          :level-data="{
            id: menuTemp.id,
            standardId: menuTemp.standardId || '',
            numId: menuTemp.numId || '',
            level: menuTemp.level || 1,
            name: menuTemp.name || '',
            content: menuTemp.content || '',
            image: menuTemp.image || '',
            ifNextLevel: menuTemp.ifNextLevel || 0,
            insertBefore: menuTemp.insertBefore, // 新增
            relativeNumId: menuTemp.numId || '', // 新增
          }"
          ref="activeComponentRef"
          :is-new="menuTemp.isNew"
          :insert-before="menuTemp.insertBefore"
          @update="handleUpdate"
        />
      </template>
      <div v-else class="empty-content">请选择左侧菜单项查看内容</div>
    </el-main>

    <!-- 右键菜单 -->
    <div
      class="mini-menu-flex"
      v-if="isShowMiniMenu"
      :style="contextMenuPosition"
    >
      <p>Debug: levelCount = {{ levelCount }}</p>
      <!-- 临时调试 -->

      <mini-opt-menu
        :visible="isShowMiniMenu"
        :menu-type="currentContextItem?.type"
        :level="Number(currentContextItem?.level) || 0"
        :level-count="levelCount"
        :position="{ left: contextMenuPosition.x, top: contextMenuPosition.y }"
        :current-item="currentContextItem"
        @update:visible="(val) => (isShowMiniMenu = val)"
        @mini-menu="handleMenuAction"
      />
    </div>
  </el-container>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from "vue";
import Sidebar from "@/layout/Sidebar.vue";
import MiniOptMenu from "@/components/miniOptMenu.vue";
import { ElMessage } from "element-plus";
import StandardPage from "../page/standardPage.vue";
import TermPage from "../page/termPage.vue";
import LevelPage from "../page/levelPage.vue";
import { MenuItem, Level, StandardForm } from "@/types/menu";
import { getStandardById, getTermsByStandardId } from "@/api/menu";
import { useRoute } from "vue-router";
import { relative } from "path";

const route = useRoute();

// 当前右键菜单项
interface ContextMenuItem {
  id: string;
  type: MenuItem["type"];
  level: 1;
}
const contextMenuStyle = computed(() => ({
  position: "fixed",
  left: `${contextMenuPosition.value.x}px`,
  top: `${contextMenuPosition.value.y}px`,
  zIndex: 2000,
}));
// 在顶部的响应式变量定义区域添加 loading
const loading = ref(false);
const menuList = ref<MenuItem[]>([]);
const formData = ref<StandardForm>({
  standardName: "",
  level: "",
  standardId: "",
  standardNumber: "",
  responsibleUnit: "",
  publicationDate: new Date(),
  implementationDate: new Date(),
  scope: "",
  departmentName: "",
  draftAuthor: "",
  draftUnit: "",
  normativeDocuments: "",
  imageList: [],
  image: "",
  file: "",
  fileName: "",
});
const menuTemp = ref<MenuItem>({
  id: "0",
  type: "standard",
  name: "",
  content: "",
  isEdit: false,
  standardId: "",
  numId: "",
  isMaxLevel: false,
  level: 0,
  // file: "",
  // fileName: "",
  image: "",
  insertBefore: false,
  relativeNumId: "",
});

// 右键菜单相关
const isShowMiniMenu = ref(false);
const contextMenuPosition = ref({
  x: 0,
  y: 0,
});

// 当前右键菜单项
const currentContextItem = ref<ContextMenuItem>({
  id: "",
  type: "standard",
  level: 1,
});

// 添加刷新方法
const refreshData = async () => {
  try {
    const prevSelectedId = menuTemp.value.id; // 保留当前选中项ID
    const prevIsEdit = menuTemp.value.isEdit; // 保留编辑状态
    const routeId = route.query.standardId;
    const prevSelectedType = menuTemp.value.type; // 保存类型

    // 临时保存而不是清空
    const tempItem = { ...menuTemp.value };
    menuTemp.value = {} as MenuItem;

    await fetchMenuData();
    if (!routeId || Array.isArray(routeId)) {
      console.warn("无效路由ID");
      return;
    }
    const standardId = Number(routeId);
    if (isNaN(standardId)) {
      console.warn("无效ID格式");
      return;
    }

    loading.value = true;

    const response = await getStandardById(standardId);
    if (response?.code === 1 && response?.data) {
      menuList.value = processMenuData(response.data);

      // 如果当前是术语管理页面，刷新术语列表
      if (menuTemp.value.type === "terms") {
        const termsResponse = await getTermsByStandardId(
          Number(menuTemp.value.standardId)
        );
        if (termsResponse?.code === 1) {
          menuTemp.value = {
            ...menuTemp.value,
            terms: termsResponse.data,
          };
        }
      }

      // 恢复选中项
      if (prevSelectedId && prevSelectedType === "level") {
        const findLevelItem = (items: MenuItem[]): MenuItem | undefined => {
          for (const item of items) {
            if (item.id === prevSelectedId && item.type === "level")
              return item;
            if (item.children?.length) {
              const found = findLevelItem(item.children);
              if (found) return found;
            }
          }
        };

        const target = findLevelItem(menuList.value);
        if (target) menuTemp.value = target;
      }
    }
  } catch (error) {
    console.error("刷新数据失败:", error);
    ElMessage.error("刷新数据失败");
  } finally {
    loading.value = false;
  }
};

// 添加查找同级最大 numId 的函数
const findMaxNumIdAtLevel = (
  items: MenuItem[],
  standardId: string = menuTemp.value.standardId, // 提供默认值
  level: number,
  currentNumId: string // 新增参数
): number => {
  let maxNum = 0;

  // 解析父级路径
  const parentParts = currentNumId.split(".").slice(0, -1); // 去掉最后一段
  const parentPath =
    parentParts.length > 0
      ? `${parentParts.join(".")}.` // "1."
      : ""; // 处理根节点
  const traverse = (menuItems: MenuItem[]) => {
    for (const item of menuItems) {
      // 匹配条件：类型、标准ID、层级、父路径、段数
      if (
        item.type === "level" &&
        item.standardId === menuTemp.value.standardId &&
        item.level === level &&
        item.numId &&
        item.numId.startsWith(parentPath) &&
        item.numId.split(".").length === parentParts.length + 1 // 段数验证
      ) {
        const numPart = item.numId.split(".").pop() || "0";
        const num = parseInt(numPart);
        if (!isNaN(num)) maxNum = Math.max(maxNum, num);
      }
      if (item.children?.length ?? 0 > 0) {
        traverse(item.children || []);
      }
    }
  };
  traverse(items);
  return maxNum;
};

// 修改 handleMenuAction 中的 add-child case
const handleMenuAction = async (action: { type: string; data?: any }) => {
  try {
    switch (action.type) {
      case "refresh": // 新增 refresh 处理
        await refreshData();
        break;
      case "insert-sibling": {
        // 新增 refresh 处理
        const currentItem = menuTemp.value;
        if (
          !currentItem?.standardId ||
          !currentItem.level ||
          !currentItem.numId
        ) {
          ElMessage.error("请先选择指标项");
          return;
        }
        // 创建插入的同级指标
        const newSiblingItem: MenuItem = {
          // id: `${Date.now()}`,
          type: "level",
          name: "新指标",
          standardId: currentItem.standardId,
          numId: currentItem.numId,
          level: currentItem.level,
          content: "",
          image: "",
          ifNextLevel: 0,
          isEdit: true,
          isNew: true,
          insertBefore: true,
          relativeNumId: currentItem.numId,
          children: [],
        }; // 在菜单列表中找到父节点并添加新项
        const findAndAddSibling = (items: MenuItem[]): boolean => {
          for (let i = 0; i < items.length; i++) {
            if (items[i].id === currentItem.id) {
              // 找到当前项，在其前插入新项
              items.splice(i - 1, 0, newSiblingItem);
              return true;
            }
            if ((items[i].children ?? []).length > 0) {
              if (findAndAddSibling(items[i].children || [])) {
                return true;
              }
            }
          }

          return false;
        };

        if (findAndAddSibling(menuList.value)) {
          menuTemp.value = newSiblingItem;
          ElMessage.success("插入指标成功");

          // 添加滚动定位逻辑
          await nextTick();
          const targetElement = document.querySelector(
            `[data-numid="${currentItem.numId}"]`
          );
          if (targetElement) {
            targetElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
              inline: "nearest",
            });
            const input = targetElement.querySelector("input");
            input?.focus();
          }
        } else {
          ElMessage.error("未找到目标位置");
        }
        break;
      }
      case "add-sibling": {
        const currentItem = menuTemp.value;
        if (
          !currentItem?.standardId ||
          !currentItem.level ||
          !currentItem.numId
        ) {
          ElMessage.error("请先选择指标项");
          return;
        }

        // 传入 currentItem.numId 用于计算父级路径
        const maxNumId = findMaxNumIdAtLevel(
          menuList.value,
          currentItem.standardId,
          currentItem.level,
          currentItem.numId // 传入当前项的 numId
        );

        // 构建新编号：父级路径 + 新序号
        const parentNumId = currentItem.numId.split(".").slice(0, -1).join(".");
        const newSiblingNumId = parentNumId
          ? `${parentNumId}.${maxNumId + 1}`
          : `${maxNumId + 1}`; // 处理根节点情况
        // 构建新的 numId
        const currentNumParts = currentItem.numId.split(".");
        currentNumParts[currentNumParts.length - 1] = (maxNumId + 1).toString();

        // 创建新的同级指标
        const newSiblingItem: MenuItem = {
          id: `${Date.now()}`,
          type: "level",
          name: "新指标",
          standardId: currentItem.standardId,
          numId: newSiblingNumId,
          level: currentItem.level,
          content: "",
          image: "",
          ifNextLevel: 0,
          isEdit: true,
          isNew: true,
          children: [],
        };

        // 在菜单列表中找到父节点并添加新项
        const findAndAddSibling = (items: MenuItem[]): boolean => {
          for (let i = 0; i < items.length; i++) {
            if (items[i].id === currentItem.id) {
              // 找到当前项，在其后插入新项
              items.splice(i + 1, 0, newSiblingItem);
              return true;
            }
            if (items[i].children && items[i].children.length > 0) {
              if (findAndAddSibling(items[i].children)) {
                return true;
              }
            }
          }

          return false;
        };

        if (findAndAddSibling(menuList.value)) {
          menuTemp.value = newSiblingItem;
          // ElMessage.success('添加同级指标成功');

          // 添加滚动定位逻辑
          await nextTick();
          const targetElement = document.querySelector(
            `[data-numid="${newSiblingNumId}"]`
          );
          if (targetElement) {
            targetElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
              inline: "nearest",
            });
            const input = targetElement.querySelector("input");
            input?.focus();
          }
        } else {
          ElMessage.error("未找到目标位置");
        }
        break;
      }

      case "add-child": {
        // 步骤1：参数校验
        const parentItem = menuTemp.value;
        if (!parentItem.numId) return;

        // 步骤2：计算子级编号（关键问题点）
        const getMaxChildNumber = () => {
          // 遍历现有子项找到最大编号
          let maxNum = 0;
          parentItem.children?.forEach((child) => {
            const parts = child.numId?.split(".") || [];
            const last = parseInt(parts[parts.length - 1]) || 0;
            maxNum = Math.max(maxNum, last);
          });
          return maxNum;
        };

        // 计算新子级编号
        const newChildNumber = getMaxChildNumber() + 1;
        const newChildNumId = `${parentItem.numId}.${newChildNumber}`;

        // 创建子级指标项
        const newChildItem: MenuItem = {
          id: `${Date.now()}`,
          type: "level",
          name: "新指标",
          standardId: parentItem.standardId,
          numId: newChildNumId,
          level: parentItem.level + 1,
          content: "",
          image: "",
          ifNextLevel: 0,
          isEdit: true,
          isNew: true,
          children: [],
        };

        // 确保父级有children数组
        if (!parentItem.children) {
          parentItem.children = [];
        }

        // 添加新子级到父级末尾
        parentItem.children.push(newChildItem);
        menuTemp.value = newChildItem;
        // ElMessage.success('添加子级指标成功');

        // 添加滚动定位逻辑
        await nextTick();
        const targetElement = document.querySelector(
          `[data-numid="${newChildNumId}"]`
        );
        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
          const input = targetElement.querySelector("input");
          input?.focus();
        }
        break;
      }
      case "edit":
        // 检查是否有传入数据
        if (!action.data) {
          console.error("编辑操作缺少数据");
          ElMessage.error("编辑操作失败：缺少必要数据");
          return;
        }

        // 更新 menuTemp
        menuTemp.value = {
          ...action.data,
          isEdit: true,
          standardId: String(action.data.standardId),
        };

        // 在菜单列表中找到并更新对应项
        const updateEditState = (items: MenuItem[]): boolean => {
          for (const item of items) {
            if (item.id === action.data.id) {
              Object.assign(item, {
                ...action.data,
                isEdit: true,
                standardId: String(action.data.standardId),
              });
              return true;
            }
            if (item.children?.length) {
              if (updateEditState(item.children)) return true;
            }
          }
          return false;
        };

        if (!updateEditState(menuList.value)) {
          console.error("未找到要编辑的项目");
          ElMessage.error("未找到要编辑的项目");
        }
        break;
    }
  } catch (error) {
    console.error("操作失败, 详细错误:", error);
    ElMessage.error("操作失败");
  }
};
// const fetchMenuList = async () => {
//   const res = await getStandardById();
//   if (res?.code === 1) {
//     menuList.value = res.data;
//   }
// };
const handleUpdate = async (updatedData: any) => {
  // 更新本地数据
  menuTemp.value = { ...menuTemp.value, ...updatedData };
  fetchMenuData();

  // 统一在此处触发保存
  await handleMenuAction({ type: "save" });
};
// 转换指标数据的递归函数
const convertLevelData = (
  level: Level,
  parentLevel: number = 1
): LevelMenuItem | null => {
  if (!level || typeof level !== "object") return null;

  const currentItem: LevelMenuItem = {
    id: String(level.id || ""),
    type: "level",
    name: level.name || "",
    standardId: String(level.standardId || ""),
    numId: level.numId || "",
    level: parentLevel,
    content: level.content || "",
    image: level.image || "",
    ifNextLevel: level.ifNextLevel || 0,

    children: [],
  };

  // 处理子级
  if (level.children && Array.isArray(level.children)) {
    currentItem.children = level.children
      .map((child) => convertLevelData(child, parentLevel + 1))
      .filter(Boolean) as LevelMenuItem[];
  }

  return currentItem;
};

// 修改processMenuData函数
const processMenuData = (data: any, standardId: number) => {
  if (!data || typeof data !== "object") {
    console.error("菜单数据格式错误:", data);
    return [];
  }

  // 原处理逻辑，处理 standard 存在的情况
  if (data && data.standard) {
    // 创建标准项
    const standardItem: MenuItem = {
      id: String(data.standard.id || ""),
      type: "standard",
      name: data.standard.standardName || "",
      standardId: String(data.standard.id || ""), // 使用 id 替代 standardId
      standardName: data.standard.standardName || "",
      responsibleUnit: data.standard.responsibleUnit || "",
      standardNumber: data.standard.standardNumber || "",
      departmentName: data.standard.departmentName || "",
      draftAuthor: data.standard.draftAuthor || "",
      draftUnit: data.standard.draftUnit || "",
      scope: data.standard.scope || "",
      level: data.standard.level || "",
      normativeDocuments: data.standard.normativeDocuments || "",
      image: data.standard.image || "",
      publicationDate: data.standard.publicationDate,
      implementationDate: data.standard.implementationDate,
      children: [],
    };

    // 处理指标数据
    if (Array.isArray(data.level1)) {
      // 处理子项可能重复的问题
      const processLevelWithDuplicateCheck = (
        levelData: any[],
        parentLevel: number = 1,
        parentNumId: string = ""
      ): MenuItem[] => {
        // 去重处理：同一级别中的项应该有唯一numId
        const uniqueChildren: Record<string, boolean> = {};
        return levelData
          .map((level) => {
            // 修正数据：确保子项的 numId 前缀与父级匹配
            if (level.numId && parentNumId) {
              // 检查子项numId是否以父级numId为前缀
              const shouldBelongToParent = level.numId.startsWith(
                parentNumId + "."
              );
              // 如果不属于当前父级，跳过此项（将在正确的父级下处理）
              if (!shouldBelongToParent) {
                return null;
              }
              // 已处理过的项跳过
              if (uniqueChildren[level.numId]) {
                return null;
              }
              uniqueChildren[level.numId] = true;
            }
            // 处理子级
            let children: MenuItem[] = [];
            if (level.level2 && Array.isArray(level.level2)) {
              children = processLevelWithDuplicateCheck(
                level.level2,
                parentLevel + 1,
                level.numId
              );
            } else if (level.level3 && Array.isArray(level.level3)) {
              children = processLevelWithDuplicateCheck(
                level.level3,
                parentLevel + 1,
                level.numId
              );
            } else if (level.level4 && Array.isArray(level.level4)) {
              children = processLevelWithDuplicateCheck(
                level.level4,
                parentLevel + 1,
                level.numId
              );
            }

            return {
              id: String(level.id || ""),
              type: "level",
              name: level.name || "",
              standardId: String(data.standard?.id || route.query.id || "0"),
              numId: level.numId || "",
              level: Number(level.level) || parentLevel,
              content: level.content || "",
              image: level.image || "",
              ifNextLevel: Number(level.ifNextLevel) || 0,
              children: children.filter(Boolean), // 过滤掉null项
            };
          })
          .filter(Boolean); // 过滤掉null项
      };

      standardItem.children = standardItem.children || [];
      standardItem.children.push(
        ...processLevelWithDuplicateCheck(data.level1)
      );
    }

    return [standardItem];
  }

  console.warn("菜单数据格式不正确:", data);
  return [];
};

// 修改fetchMenuData函数
const fetchMenuData = async () => {
  try {
    loading.value = true;
    // 1. 记住当前选中项
    const prevSelectedId = menuTemp.value.id;
    const prevSelectedType = menuTemp.value.type;
    // 从路由参数获取standardId
    const standardId = route.query.standardId
      ? Number(route.query.standardId)
      : 0;
    if (!standardId) {
      console.warn("未获取到标准id");
      return;
    }

    // 使用路由中的standardId
    const response = await getStandardById(standardId);

    if (response?.code === 1 && response?.data) {
      // 将standardId传递给processMenuData
      menuList.value = processMenuData(response.data, standardId);

      // 2. 恢复选中项
      let found = false;
      if (prevSelectedId && prevSelectedType) {
        // 递归查找
        const findItem = (items: MenuItem[]): MenuItem | undefined => {
          for (const item of items) {
            if (item.id === prevSelectedId && item.type === prevSelectedType) {
              return item;
            }
            if (item.children?.length) {
              const foundChild = findItem(item.children);
              if (foundChild) return foundChild;
            }
          }
        };
        const target = findItem(menuList.value);
        if (target) {
          menuTemp.value = target;
          found = true;
        }
      }
      // 3. 如果没找到，fallback 到标准项
      if (!found && menuList.value.length > 0) {
        menuTemp.value = menuList.value[0];
      }

      // 获取术语数据
      await getTermsData(standardId);
    } else {
      console.warn("获取菜单数据失败:", response?.msg);
      menuList.value = [];
      ElMessage.warning("获取菜单数据失败");
    }
  } catch (error) {
    // 错误处理...
  } finally {
    loading.value = false;
  }
};

// 添加独立的术语数据获取函数
const getTermsData = async (standardId: number) => {
  try {
    const termsResponse = await getTermsByStandardId(standardId);

    if (termsResponse?.code === 1) {
      // 更新标准项的术语数据
      if (menuList.value.length > 0 && menuList.value[0].type === "standard") {
        menuList.value[0].terms = termsResponse.data;
      }

      // 更新当前选中的术语数据
      if (menuTemp.value.type === "terms") {
        menuTemp.value = {
          ...menuTemp.value,
          terms: termsResponse.data,
        };
      }
    }
  } catch (error) {
    console.error("获取术语数据失败:", error);
  }
};
// 替换原有的 levelCount 计算属性
const levelCount = computed(() => {
  let count = 0;
  console.log("计算 levelCount...");

  const traverse = (items: MenuItem[]) => {
    for (const item of items) {
      console.log("遍历项目:", item.id, item.type);

      if (item.type === "level") {
        count++;
      }
      if (item.children && item.children.length) {
        traverse(item.children);
      }
    }
  };

  traverse(menuList.value);
  return count;
});
// 修改监听路由参数的逻辑
watch(
  () => route.query.standardId,
  async (newStandardId) => {
    if (newStandardId) {
      const standardId = Number(newStandardId);
      if (!isNaN(standardId)) {
        await fetchMenuData();
      }
    }
  },
  { immediate: true } // 立即执行一次
);
// // 监听路由参数变化以重新获取数据
// watch(
//   () => route.query.standardId,
//   async (newStandardId) => {
//     // Convert standardId to a number
//     const standardId = Array.isArray(route.query.standardId)
//       ? Number(route.query.standardId[0])
//       : Number(route.query.standardId);

//     if (standardId) {
//       try {
//         const termsResponse = await getTermsByStandardId(standardId); // 传递 number 类型
//         if (termsResponse?.code === 1) {
//           menuTemp.value = {
//             ...menuTemp.value,
//             terms: termsResponse.data,
//           };
//         } else {
//           ElMessage.warning("获取术语列表失败");
//         }
//       } catch (error) {
//         ElMessage.error("获取术语列表失败");
//       }
//     }
//   },
//   { immediate: true }
// );

// 在组件挂载时获取数据
onMounted(() => {
  // 如果路由参数存在，直接获取数据
  if (route.query.standardId) {
    fetchMenuData();
  }
});

watch(
  () => menuTemp.value,
  (newVal) => {
    if (newVal.type === "terms") {
      getTermsData();
    } else if (newVal.type === "standard") {
      // 更新标准详情数据
      formData.value = {
        id: newVal.id || "",
        standardName: newVal.standardName || "",
        level: newVal.level || "",
        standardId: newVal.standardId || "",
        standardNumber: newVal.standardNumber || "",
        responsibleUnit: newVal.responsibleUnit || "",
        publicationDate: newVal.publicationDate || "",
        implementationDate: newVal.implementationDate || "",
        scope: newVal.scope || "",
        departmentName: newVal.departmentName || "",
        draftAuthor: newVal.draftAuthor || "",
        draftUnit: newVal.draftUnit || "",
        normativeDocuments: newVal.normativeDocuments || "",
        imageList: newVal.imageList || [],
        imageUrl: newVal.imageList?.[0] || "",
        file: newVal.file || "",
        fileName: newVal.fileName || "",
      };
    }
  },
  { immediate: true, deep: true }
);

// 拖拽相关状态
const asideWidth = ref(
  localStorage.getItem("asideWidth")
    ? parseInt(localStorage.getItem("asideWidth")!)
    : 200
); // 添加本地存储
const isDragging = ref(false);
let startX = 0;
let startWidth = 0;

// 开始拖拽
const startDrag = (e: MouseEvent) => {
  e.preventDefault();
  isDragging.value = true;
  startX = e.clientX;
  startWidth = asideWidth.value;

  // 添加事件监听
  document.addEventListener("mousemove", handleDrag);
  document.addEventListener("mouseup", stopDrag);

  // 添加拖拽时的全局样式
  document.body.style.cursor = "col-resize";
  document.body.classList.add("is-dragging");
};

// 处理拖拽
const handleDrag = (e: MouseEvent) => {
  if (!isDragging.value) return;

  const delta = e.clientX - startX;
  const newWidth = Math.min(Math.max(150, startWidth + delta), 500);

  // 使用 requestAnimationFrame 优化性能
  requestAnimationFrame(() => {
    asideWidth.value = newWidth;
  });
};

// 停止拖拽
const stopDrag = () => {
  isDragging.value = false;

  // 移除事件监听
  document.removeEventListener("mousemove", handleDrag);
  document.removeEventListener("mouseup", stopDrag);

  // 恢复全局样式
  document.body.style.cursor = "";
  document.body.classList.remove("is-dragging");

  // 保存宽度到本地存储
  localStorage.setItem("asideWidth", asideWidth.value.toString());
};

// 组件卸载时清理
onUnmounted(() => {
  document.removeEventListener("mousemove", handleDrag);
  document.removeEventListener("mouseup", stopDrag);
});

watch(
  () => menuTemp.value,
  () => {
    nextTick(() => {
      // 确保拖拽条仍然可用
      const resizeHandle = document.querySelector(".resize-handle");
      if (!resizeHandle) {
        console.warn("拖拽条未找到！请检查模板内是否存在 .resize-handle");
      }
    });
  }
);
</script>

<style scoped>
.layout-container {
  flex: 1;
  display: flex;
  flex-direction: row;
  overflow: hidden;
}

.el-aside {
  border-right: 1px solid #dcdfe6;
  background-color: #fff;
}

.el-main {
  padding: 20px;
  overflow-y: auto;
}

.content-form {
  max-width: 800px;
  margin: 0 auto;
}

.preview-content {
  padding: 20px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background-color: #fff;
}

.empty-content {
  text-align: center;
  color: #909399;
  font-size: 14px;
  padding: 40px 0;
}

.mini-menu-flex {
  position: fixed;
}

.aside-header {
  padding: 10px;
  border-bottom: 1px solid #eee;
}
.resizable-aside {
  position: relative;
  transition: width 0.2s ease;
  height: 100vh;
}

.drag-bar {
  position: absolute;
  right: -2px;
  top: 0;
  bottom: 0;
  width: 4px;
  background-color: #dcdfe6;
  cursor: col-resize;
  z-index: 1000;
  transition: background-color 0.2s;
}

.drag-bar:hover,
.drag-bar:active {
  background-color: #409eff;
}
.resizable-aside {
  position: relative;
  height: 100vh;
  transition: width 0.05s cubic-bezier(0.4, 0, 0.2, 1);
  border-right: 1px solid #dcdfe6;
  background-color: #fff;
}

/* 拖拽时禁用过渡效果 */
.resizable-aside.is-dragging {
  transition: none;
}
.resize-handle {
  position: absolute;
  top: 0;
  right: -3px;
  width: 6px;
  height: 100%;
  background-color: transparent;
  cursor: col-resize;
  z-index: 100;
  transition: background-color 0.2s;
}

.resize-handle:hover,
.resize-handle.is-active {
  background-color: var(--el-color-primary, #409eff);
  opacity: 0.4;
}

/* 拖拽时的全局样式 */
:global(body.is-dragging) {
  user-select: none;
  /* cursor: col-resize !important; */
}

/* 拖拽时的遮罩层 */
.resizable-aside.is-dragging::after {
  content: "";
  position: fixed;
  inset: 0;
  z-index: 9999;
  background-color: transparent;
}

/* 优化滚动条样式 */
.resizable-aside :deep(.el-scrollbar) {
  height: 100%;
}

.resizable-aside :deep(.el-scrollbar__wrap) {
  overflow-x: hidden;
}
</style>
